// Phase 38 · 23b — the breakdown widget (status, and reused for priority).
//
// This was a donut. Five slices needed five colours and still left 12% and 9%
// hard to tell apart, and the legend underneath repeated every label a second
// time. A ranked list gives the count, the share and the ordering in one pass,
// in one colour per row that only has to distinguish rows from each other.
//
// The donut, pie and bar renderings are still here: an admin who explicitly
// picked one in Dashboard Management keeps it. Only the DEFAULT changed.

import { DonutChart, PieChart, BarChart, PieChartDataPoint } from '../charts';
import { ranked, leader } from '../../features/dashboard/widgetFindings';
import { WidgetShell, WidgetEmpty, RankedList, Found } from './WidgetShell';

type StatusData = PieChartDataPoint;

interface TicketsByStatusWidgetProps {
  title?: string;
  data?: StatusData[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** `ranked` is the 23b default; the chart types remain for configured widgets. */
  chartType?: 'ranked' | 'donut' | 'pie' | 'bar';
  /** Noun for the foot band and subtitle — "tickets" unless told otherwise. */
  unit?: string;
}

export function TicketsByStatusWidget({
  data,
  loading = false,
  error,
  onRetry,
  className = '',
  chartType = 'ranked',
  title,
  unit = 'tickets',
}: TicketsByStatusWidgetProps) {
  const total = data ? data.reduce((sum, item) => sum + item.value, 0) : 0;
  const rows = ranked(data?.map((d) => ({ label: d.name, value: d.value, color: d.color })));
  const top = leader(rows);

  const finding =
    top && top.share !== null ? (
      <>
        <Found>
          {top.label} is the largest at {top.value}
        </Found>{' '}
        — {Math.round(top.share)}% of the {total} {unit} counted here.
      </>
    ) : null;

  return (
    <WidgetShell
      title={title || 'Tickets by Status'}
      subtitle={loading ? undefined : `${total} ${unit}`}
      loading={loading}
      error={error}
      onRetry={onRetry}
      className={className}
      finding={finding}
      empty={
        !data || data.length === 0 ? (
          <WidgetEmpty
            title="No breakdown yet"
            hint={`The split appears once there are ${unit} to count.`}
          />
        ) : undefined
      }
      bodyClassName={chartType === 'ranked' ? 'overflow-y-auto co-scrollbar-none' : 'p-3'}
    >
      {chartType === 'ranked' ? (
        <RankedList rows={rows} />
      ) : chartType === 'pie' ? (
        <PieChart data={data!} showLegend={false} showLabels />
      ) : chartType === 'bar' ? (
        <BarChart
          data={data!.map((item) => ({ name: item.name, value: item.value, color: item.color ?? '' }))}
          bars={[{ dataKey: 'value', name: 'Count' }]}
          showLegend={false}
          colorByValue
        />
      ) : (
        <DonutChart data={data!} showLegend={false} showLabels />
      )}
    </WidgetShell>
  );
}
